import { DEFAULT_LOCALE } from '../i18n/config';

/**
 * Post id -> localized slug, for every locale.
 *
 * Post bodies are loaded one locale at a time (see ./index.js), but hreflang
 * tags and the language switcher need to know every locale's URL for the page
 * you are on. This map is small and static so it can answer that without
 * pulling in six other locales' article text.
 *
 * The id doubles as the English slug, which keeps the already-indexed English
 * URLs unchanged and lets in-content links be written as `/blog/<id>`.
 */
export const POST_SLUGS = {
  'what-is-manifestation': {
    en: 'what-is-manifestation',
    tr: 'manifestasyon-nedir',
    es: 'que-es-la-manifestacion',
    fr: 'qu-est-ce-que-la-manifestation',
    de: 'was-ist-manifestation',
    it: 'cos-e-la-manifestazione',
    nl: 'wat-is-manifesteren',
  },
  'how-to-manifest': {
    en: 'how-to-manifest',
    tr: 'nasil-manifest-edilir',
    es: 'como-manifestar',
    fr: 'comment-manifester',
    de: 'richtig-manifestieren',
    it: 'come-manifestare',
    nl: 'hoe-manifesteren',
  },
  '369-manifestation-method': {
    en: '369-manifestation-method',
    tr: '369-manifestasyon-metodu',
    es: 'metodo-369-manifestacion',
    fr: 'methode-369-manifestation',
    de: '369-methode-manifestation',
    it: 'metodo-369-manifestazione',
    nl: '369-methode-manifesteren',
  },
  'daily-affirmations': {
    en: 'daily-affirmations',
    tr: 'gunluk-olumlamalar',
    es: 'afirmaciones-diarias',
    fr: 'affirmations-quotidiennes',
    de: 'taegliche-affirmationen',
    it: 'affermazioni-quotidiane',
    nl: 'dagelijkse-affirmaties',
  },
  'how-to-write-affirmations': {
    en: 'how-to-write-affirmations',
    tr: 'olumlama-nasil-yazilir',
    es: 'como-escribir-afirmaciones',
    fr: 'comment-ecrire-des-affirmations',
    de: 'affirmationen-schreiben',
    it: 'come-scrivere-affermazioni',
    nl: 'affirmaties-schrijven',
  },
  'vision-board-guide': {
    en: 'vision-board-guide',
    tr: 'vizyon-panosu-rehberi',
    es: 'guia-tablero-de-vision',
    fr: 'guide-tableau-de-visualisation',
    de: 'vision-board-anleitung',
    it: 'guida-vision-board',
    nl: 'vision-board-gids',
  },
  'scripting-manifestation': {
    en: 'scripting-manifestation',
    tr: 'scripting-manifestasyon',
    es: 'scripting-manifestacion',
    fr: 'scripting-manifestation',
    de: 'scripting-manifestation',
    it: 'scripting-manifestazione',
    nl: 'scripting-manifesteren',
  },
  'dream-interpretation-guide': {
    en: 'dream-interpretation-guide',
    tr: 'ruya-yorumu-rehberi',
    es: 'guia-interpretacion-de-suenos',
    fr: 'guide-interpretation-des-reves',
    de: 'traumdeutung-anleitung',
    it: 'guida-interpretazione-dei-sogni',
    nl: 'droomuitleg-gids',
  },
  'law-of-attraction': {
    en: 'law-of-attraction',
    tr: 'cekim-yasasi',
    es: 'ley-de-la-atraccion',
    fr: 'loi-de-l-attraction',
    de: 'gesetz-der-anziehung',
    it: 'legge-dell-attrazione',
    nl: 'wet-van-aantrekking',
  },
  'letting-go-manifestation': {
    en: 'letting-go-manifestation',
    tr: 'manifestasyonda-birakmak',
    es: 'soltar-en-la-manifestacion',
    fr: 'lacher-prise-manifestation',
    de: 'loslassen-beim-manifestieren',
    it: 'lasciare-andare-manifestazione',
    nl: 'loslaten-bij-manifesteren',
  },
  'morning-manifestation-routine': {
    en: 'morning-manifestation-routine',
    tr: 'sabah-manifestasyon-rutini',
    es: 'rutina-matutina-de-manifestacion',
    fr: 'routine-matinale-de-manifestation',
    de: 'morgenroutine-manifestation',
    it: 'routine-mattutina-manifestazione',
    nl: 'ochtendroutine-manifesteren',
  },
  'self-love-affirmations': {
    en: 'self-love-affirmations',
    tr: 'oz-sevgi-olumlamalari',
    es: 'afirmaciones-de-amor-propio',
    fr: 'affirmations-amour-de-soi',
    de: 'selbstliebe-affirmationen',
    it: 'affermazioni-amore-proprio',
    nl: 'zelfliefde-affirmaties',
  },
  'money-affirmations': {
    en: 'money-affirmations',
    tr: 'para-olumlamalari',
    es: 'afirmaciones-para-el-dinero',
    fr: 'affirmations-pour-l-argent',
    de: 'geld-affirmationen',
    it: 'affermazioni-sul-denaro',
    nl: 'geld-affirmaties',
  },
};

/** Post ids in display order (most fundamental first). */
export const POST_IDS = Object.keys(POST_SLUGS);

/** The slug a post is published under in a given locale. */
export function slugFor(id, locale) {
  const entry = POST_SLUGS[id];
  if (!entry) return null;
  return entry[locale] || entry[DEFAULT_LOCALE];
}

/** Reverse lookup: which post is this locale's slug pointing at? */
export function idFromSlug(slug, locale) {
  const hit = POST_IDS.find((id) => slugFor(id, locale) === slug);
  return hit || null;
}
